import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Clock, 
  Plus, 
  Trash2, 
  Settings, 
  MapPin, 
  Users, 
  Calendar,
  Save,
  ArrowLeft,
  FileText
} from 'lucide-react';
import type { ScheduleConfiguration, DailySchedule } from '../../types';
import RoomsConfiguration from '../../components/RoomsConfiguration';
import FormsConfiguration from '../../components/FormsConfiguration';
import ParticipantsConfiguration from '../../components/ParticipantsConfiguration';
import { scheduleApi } from '../../services/api';

const ScheduleConfigurator: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'general' | 'schedule' | 'rooms' | 'forms' | 'participants'>('general');
  const [isLoading, setIsLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Mock event data - in real app, fetch from API
  const event = {
    id: eventId || '1',
    title: 'Fall Portfolio Reviews',
    type: 'in-person' as 'online' | 'in-person' | 'hybrid',
    startDate: new Date('2025-09-15'),
    endDate: new Date('2025-09-17'),
    location: 'Design Building',
  };

  const [scheduleConfig, setScheduleConfig] = useState<Partial<ScheduleConfiguration>>({
    reviewDuration: 20,
    breakBetweenReviews: 5,
    maxConcurrentReviews: 4,
    allowSlotEditing: true,
    autoReleaseMinutes: 60,
    timezone: 'America/New_York',
    setupTimeMinutes: 15,
    cleanupTimeMinutes: 15,
    dailySchedules: []
  });
  const [hasLoadedConfig, setHasLoadedConfig] = useState(false);

  // Load existing schedule configuration when component mounts
  useEffect(() => {
    const loadScheduleConfig = async () => {
      if (!eventId || hasLoadedConfig) return;
      
      try {
        setIsLoading(true);
        const existingConfig = await scheduleApi.get(eventId);
        
        // Convert the loaded configuration to match our component state
        setScheduleConfig({
          ...existingConfig,
          dailySchedules: existingConfig.dailySchedules?.map(schedule => ({
            ...schedule,
            id: schedule.id?.toString() || Date.now().toString(),
            date: new Date(schedule.date)
          })) || []
        });
        setHasLoadedConfig(true);
      } catch (error) {
        console.error('Error loading schedule configuration:', error);
        // If no configuration exists yet, keep the default values
        setHasLoadedConfig(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadScheduleConfig();
  }, [eventId, hasLoadedConfig]);

    const addDailySchedule = () => {
    const newSchedule: DailySchedule = {
      id: Date.now().toString(),
      date: new Date(event.startDate),
      startTime: '09:00',
      endTime: '17:00',
      isActive: true,
      notes: ''
    };

    setScheduleConfig(prev => ({
      ...prev,
      dailySchedules: [...(prev.dailySchedules || []), newSchedule],
    }));
  };

  const updateDailySchedule = (scheduleId: string, updates: Partial<DailySchedule>) => {
    setScheduleConfig(prev => ({
      ...prev,
      dailySchedules: prev.dailySchedules?.map(schedule =>
        schedule.id === scheduleId ? { ...schedule, ...updates } : schedule
      ) || [],
    }));
  };

  const deleteDailySchedule = (scheduleId: string) => {
    setScheduleConfig(prev => ({
      ...prev,
      dailySchedules: prev.dailySchedules?.filter(schedule => schedule.id !== scheduleId) || [],
    }));
  };

  const generateTimeSlots = () => {
    // Calculate how many slots can be created based on configuration
    const totalSlots = scheduleConfig.dailySchedules?.reduce((total, schedule) => {
      if (!schedule.isActive) return total;
      
      const startTime = new Date(`${schedule.date.toDateString()} ${schedule.startTime}`);
      const endTime = new Date(`${schedule.date.toDateString()} ${schedule.endTime}`);
      const duration = scheduleConfig.reviewDuration || 20;
      const breakTime = scheduleConfig.breakBetweenReviews || 5;
      const slotDuration = duration + breakTime;
      
      const totalMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
      const slotsPerDay = Math.floor(totalMinutes / slotDuration);
      const concurrentReviews = schedule.maxConcurrentReviews || scheduleConfig.maxConcurrentReviews || 4;
      
      return total + (slotsPerDay * concurrentReviews);
    }, 0) || 0;

    return totalSlots;
  };

  const generateDetailedTimeSlots = () => {
    if (!scheduleConfig.dailySchedules) return [];
    
    return scheduleConfig.dailySchedules
      .filter(schedule => schedule.isActive)
      .map(schedule => {
        const startTime = new Date(`${schedule.date.toDateString()} ${schedule.startTime}`);
        const endTime = new Date(`${schedule.date.toDateString()} ${schedule.endTime}`);
        const duration = scheduleConfig.reviewDuration || 20;
        const breakTime = scheduleConfig.breakBetweenReviews || 5;
        const slotDuration = duration + breakTime;
        const setupTime = scheduleConfig.setupTimeMinutes || 0;
        const cleanupTime = scheduleConfig.cleanupTimeMinutes || 0;
        const concurrentReviews = schedule.maxConcurrentReviews || scheduleConfig.maxConcurrentReviews || 4;
        
        // Adjust start and end times for setup/cleanup
        const adjustedStartTime = new Date(startTime.getTime() + setupTime * 60000);
        const adjustedEndTime = new Date(endTime.getTime() - cleanupTime * 60000);
        
        const totalMinutes = (adjustedEndTime.getTime() - adjustedStartTime.getTime()) / (1000 * 60);
        const slotsPerDay = Math.floor(totalMinutes / slotDuration);
        
        const slots = [];
        for (let i = 0; i < slotsPerDay; i++) {
          const slotStart = new Date(adjustedStartTime.getTime() + i * slotDuration * 60000);
          const slotEnd = new Date(slotStart.getTime() + duration * 60000);
          
          slots.push({
            id: `${schedule.id}-slot-${i}`,
            startTime: slotStart.toLocaleTimeString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit',
              hour12: true 
            }),
            endTime: slotEnd.toLocaleTimeString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit',
              hour12: true 
            }),
            duration: duration,
            concurrentReviews: concurrentReviews
          });
        }
        
        return {
          ...schedule,
          slots,
          totalSlots: slotsPerDay * concurrentReviews
        };
      });
  };

  const handleSave = async () => {
    setIsLoading(true);
    setSaveSuccess(false);
    try {
      // Validate required fields
      if (scheduleConfig.reviewDuration === undefined || scheduleConfig.reviewDuration === null || 
          scheduleConfig.breakBetweenReviews === undefined || scheduleConfig.breakBetweenReviews === null || 
          scheduleConfig.maxConcurrentReviews === undefined || scheduleConfig.maxConcurrentReviews === null || 
          !scheduleConfig.timezone ||
          scheduleConfig.setupTimeMinutes === undefined || scheduleConfig.setupTimeMinutes === null || 
          scheduleConfig.cleanupTimeMinutes === undefined || scheduleConfig.cleanupTimeMinutes === null ||
          scheduleConfig.allowSlotEditing === undefined || scheduleConfig.allowSlotEditing === null) {
        throw new Error('Missing required configuration fields');
      }

      // Save schedule configuration to the database  
      const scheduleData = {
        reviewDuration: scheduleConfig.reviewDuration!,
        breakBetweenReviews: scheduleConfig.breakBetweenReviews!,
        maxConcurrentReviews: scheduleConfig.maxConcurrentReviews!,
        allowSlotEditing: scheduleConfig.allowSlotEditing!,
        autoReleaseMinutes: scheduleConfig.autoReleaseMinutes,
        timezone: scheduleConfig.timezone!,
        setupTimeMinutes: scheduleConfig.setupTimeMinutes!,
        cleanupTimeMinutes: scheduleConfig.cleanupTimeMinutes!,
        dailySchedules: scheduleConfig.dailySchedules?.map(schedule => ({
          date: schedule.date.toISOString().split('T')[0], // Convert to ISO date string (YYYY-MM-DD)
          startTime: schedule.startTime,
          endTime: schedule.endTime,
          isActive: schedule.isActive,
          maxConcurrentReviews: schedule.maxConcurrentReviews,
          notes: schedule.notes
        }))
      };
      
      await scheduleApi.upsert(eventId || '1', scheduleData as any);
      
      // Generate time slots after saving configuration
      await scheduleApi.generateSlots(eventId || '1');
      
      console.log('Schedule configuration saved successfully');
      
      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000); // Hide after 3 seconds
      
      // Stay on the page to continue editing
      // navigate(`/admin/events/${eventId}`); // Removed automatic redirect
    } catch (error) {
      console.error('Error saving schedule configuration:', error);
      // TODO: Show error message to user
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <button
            onClick={() => navigate('/admin/events')}
            className="mr-4 p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Schedule Configuration</h1>
            <p className="text-gray-600 mt-1">{event.title} - {event.type} event</p>
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <Calendar className="h-5 w-5 text-blue-600 mr-2" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Event Duration: {event.startDate.toLocaleDateString()} - {event.endDate.toLocaleDateString()}
              </p>
              <p className="text-sm text-blue-700">
                Configure daily schedules, time slots, and room assignments below
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'general', label: 'General Settings', icon: Settings },
            { id: 'schedule', label: 'Daily Schedules', icon: Clock },
            { id: 'rooms', label: 'Rooms & Spaces', icon: MapPin },
            { id: 'forms', label: 'Forms', icon: FileText },
            { id: 'participants', label: 'Participants', icon: Users },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="mr-2 h-5 w-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* General Settings Tab */}
      {activeTab === 'general' && (
        <div className="space-y-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Review Settings</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Review Duration (minutes)
                </label>
                <input
                  type="number"
                  value={scheduleConfig.reviewDuration}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, reviewDuration: parseInt(e.target.value) }))}
                  min="5"
                  max="180"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Default: 20 minutes</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Break Between Reviews (minutes)
                </label>
                <input
                  type="number"
                  value={scheduleConfig.breakBetweenReviews}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, breakBetweenReviews: parseInt(e.target.value) }))}
                  min="0"
                  max="30"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Time between review sessions</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Concurrent Reviews
                </label>
                <input
                  type="number"
                  value={scheduleConfig.maxConcurrentReviews}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, maxConcurrentReviews: parseInt(e.target.value) }))}
                  min="1"
                  max="50"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Default number of parallel reviews</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  value={scheduleConfig.timezone}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, timezone: e.target.value }))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="America/New_York">Eastern Time (EST/EDT)</option>
                  <option value="America/Chicago">Central Time (CST/CDT)</option>
                  <option value="America/Denver">Mountain Time (MST/MDT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PST/PDT)</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Setup Time (minutes)
                </label>
                <input
                  type="number"
                  value={scheduleConfig.setupTimeMinutes}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, setupTimeMinutes: parseInt(e.target.value) }))}
                  min="0"
                  max="60"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Buffer time before first review</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cleanup Time (minutes)
                </label>
                <input
                  type="number"
                  value={scheduleConfig.cleanupTimeMinutes}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, cleanupTimeMinutes: parseInt(e.target.value) }))}
                  min="0"
                  max="60"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Buffer time after last review</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Reviewer Settings</h2>
            
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="allowSlotEditing"
                  checked={scheduleConfig.allowSlotEditing}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, allowSlotEditing: e.target.checked }))}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="allowSlotEditing" className="ml-3 block text-sm text-gray-900">
                  Allow reviewers to edit their schedule
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Auto-release slots (minutes before review)
                </label>
                <input
                  type="number"
                  value={scheduleConfig.autoReleaseMinutes}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, autoReleaseMinutes: parseInt(e.target.value) }))}
                  min="0"
                  max="1440"
                  placeholder="60"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty to disable auto-release</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Daily Schedules Tab */}
      {activeTab === 'schedule' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Daily Schedules</h2>
              <p className="text-gray-600 text-sm mt-1">Configure daily time slots for each day of the event</p>
            </div>
            <button
              onClick={addDailySchedule}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Day
            </button>
          </div>

          {scheduleConfig.dailySchedules?.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No daily schedules configured</h3>
              <p className="text-gray-600 mb-4">Add daily schedules to define when reviews can take place</p>
              <button
                onClick={addDailySchedule}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Day
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {scheduleConfig.dailySchedules?.map((schedule) => (
                <div key={schedule.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={schedule.isActive}
                        onChange={(e) => schedule.id && updateDailySchedule(schedule.id, { isActive: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                      />
                      <h3 className="text-lg font-medium text-gray-900">
                        {schedule.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                      </h3>
                    </div>
                    <button
                      onClick={() => schedule.id && deleteDailySchedule(schedule.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                      <input
                        type="date"
                        value={schedule.date.toISOString().split('T')[0]}
                        onChange={(e) => schedule.id && updateDailySchedule(schedule.id, { date: new Date(e.target.value) })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                      <input
                        type="time"
                        value={schedule.startTime}
                        onChange={(e) => schedule.id && updateDailySchedule(schedule.id, { startTime: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                      <input
                        type="time"
                        value={schedule.endTime}
                        onChange={(e) => schedule.id && updateDailySchedule(schedule.id, { endTime: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Concurrent</label>
                      <input
                        type="number"
                        value={schedule.maxConcurrentReviews || scheduleConfig.maxConcurrentReviews}
                        onChange={(e) => schedule.id && updateDailySchedule(schedule.id, { maxConcurrentReviews: parseInt(e.target.value) })}
                        min="1"
                        max="50"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <input
                      type="text"
                      value={schedule.notes}
                      onChange={(e) => schedule.id && updateDailySchedule(schedule.id, { notes: e.target.value })}
                      placeholder="Optional notes for this day"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Schedule Summary */}
          {scheduleConfig.dailySchedules && scheduleConfig.dailySchedules.length > 0 && (
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-blue-900 mb-4 flex items-center">
                  <Users className="h-5 w-5 mr-2" />
                  Schedule Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-blue-700 font-medium">Total Available Slots</p>
                    <p className="text-2xl font-bold text-blue-900">{generateTimeSlots()}</p>
                  </div>
                  <div>
                    <p className="text-blue-700 font-medium">Active Days</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {scheduleConfig.dailySchedules.filter(s => s.isActive).length}
                    </p>
                  </div>
                  <div>
                    <p className="text-blue-700 font-medium">Review Duration</p>
                    <p className="text-2xl font-bold text-blue-900">{scheduleConfig.reviewDuration}min</p>
                  </div>
                </div>
              </div>

              {/* Time Slots Preview */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <Clock className="h-5 w-5 mr-2" />
                    Generated Time Slots
                  </h3>
                  <div className="text-sm text-gray-500">
                    Based on {scheduleConfig.reviewDuration}min reviews + {scheduleConfig.breakBetweenReviews}min breaks
                  </div>
                </div>
                
                {generateDetailedTimeSlots().map((daySchedule) => (
                  <div key={daySchedule.id} className="mb-6 last:mb-0">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">
                        {daySchedule.date.toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </h4>
                      <div className="text-sm text-gray-600">
                        {daySchedule.slots.length} time slots × {daySchedule.maxConcurrentReviews || scheduleConfig.maxConcurrentReviews} concurrent = {daySchedule.totalSlots} total slots
                      </div>
                    </div>
                    
                    {daySchedule.slots.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {daySchedule.slots.map((slot, index) => (
                          <div 
                            key={slot.id}
                            className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm"
                          >
                            <div className="font-medium text-gray-900 mb-1">
                              Slot {index + 1}
                            </div>
                            <div className="text-gray-600">
                              {slot.startTime} - {slot.endTime}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {slot.concurrentReviews} concurrent reviews
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-gray-500 text-sm italic">
                        No time slots can be generated with current settings
                      </div>
                    )}
                  </div>
                ))}
                
                {generateDetailedTimeSlots().length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p>Configure and activate daily schedules to see generated time slots</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rooms Tab */}
      {activeTab === 'rooms' && (
        <RoomsConfiguration eventId={eventId || '1'} />
      )}

      {/* Forms Tab */}
      {activeTab === 'forms' && (
        <FormsConfiguration eventId={eventId || '1'} />
      )}

      {/* Participants Tab */}
      {activeTab === 'participants' && (
        <ParticipantsConfiguration eventId={eventId || '1'} />
      )}

      {/* Save Button */}
      <div className="flex justify-end pt-8 border-t border-gray-200">
        <div className="flex space-x-4">
          <button
            onClick={() => navigate('/admin/events')}
            className="px-6 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving Configuration...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Schedule Configuration
              </>
            )}
          </button>
          {saveSuccess && (
            <div className="flex items-center text-green-600 bg-green-50 border border-green-200 rounded-md px-3 py-2 ml-4">
              <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Configuration saved successfully!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleConfigurator;